import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SwitchSetting } from './SwitchSetting';
import { useDebounce } from '../../../hooks/useDebounce';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { handleEmailSMSAnalytics, getErrorMessage } from '../../../utils';
import { useAppSelector } from '../../../hooks/redux';

export const Notification: FC = () => {
  const { t } = useTranslation();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const methods = useForm();
  const { userInfoState, isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const { setValue } = methods;
  const [setEmailNotification] = userInfoApi.useSetEmailNotificationMutation();
  const [setPushNotification] = userInfoApi.useSetPushNotificationMutation();

  useEffect(() => {
    if (isUserInfoDownloaded) {
      setValue(
        'emailNotification',
        (userInfoState?.EmailSettings?.Accept ||
          userInfoState?.EmailSettings?.News ||
          userInfoState?.EmailSettings?.NoBalance ||
          userInfoState?.EmailSettings?.Repairs) &&
          userInfoState?.EmailSettings?.IsConfirmed,
      );
      setValue(
        'phoneNotification',
        userInfoState?.SmsSettings?.News ||
          userInfoState?.SmsSettings?.NoBalance ||
          userInfoState?.SmsSettings?.Repairs ||
          userInfoState?.SmsSettings?.Accept,
      );
      setValue(
        'pushNotification',
        userInfoState?.PushSettings?.News ||
          userInfoState?.PushSettings?.NoBalance ||
          userInfoState?.PushSettings?.Repairs ||
          userInfoState?.PushSettings?.Accept,
      );
    }
  }, [isUserInfoDownloaded]);

  const handleEmailChange = useDebounce(async (isChecked: boolean) => {
    try {
      await setEmailNotification({ isNotification: isChecked ? '1' : '0' })
        .unwrap()
        .then(() => {
          if (isChecked) {
            customEnqueueSnackbar(t('snackbar.emailInformingIsOn'), 'success');
          } else {
            customEnqueueSnackbar(t('snackbar.emailInformingIsOff'), 'warning');
          }
        });
      handleEmailSMSAnalytics('email', isChecked);
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(t(message), 'error');
    }
  }, 800);

  const handlePushChange = useDebounce(async (isChecked: boolean) => {
    try {
      await setPushNotification({ isNotification: isChecked })
        .unwrap()
        .then(() => {
          if (isChecked) {
            customEnqueueSnackbar(t('snackbar.pushInformingIsOn'), 'success');
          } else {
            customEnqueueSnackbar(t('snackbar.pushInformingIsOff'), 'warning');
          }
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(t(message), 'error');
    }
  }, 800);

  return (
    <div>
      <div className="notification__settings-container">
        <FormProvider {...methods}>
          <SwitchSetting
            title={t('accountSettings.emailInforming')}
            value={userInfoState?.EmailSettings?.Email || t('accountSettings.emailNotAdded')}
            isDisabled={!userInfoState?.EmailSettings?.IsConfirmed}
            switchName="emailNotification"
            handleChange={handleEmailChange}
          />
          <SwitchSetting
            title={t('accountSettings.pushNotifications')}
            isDisabled={!userInfoState?.PushSettings}
            switchName="pushNotification"
            handleChange={handlePushChange}
          />
        </FormProvider>
      </div>
    </div>
  );
};
